// Chemin: C:\gestion-copro\src\components\calendrier\EcheancesWidget.tsx
// Widget "Prochaines échéances" pour le Dashboard
// Session 94 : Création initiale
// Session 94 : Correction couleurs J-XX (rouge=J-1/J-0, orange=J-7, ambre=J-14, gris sinon)

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Evenement,
  TYPE_EVENEMENT_LABELS,
  TYPE_EVENEMENT_COULEURS,
  TypeEvenement,
} from '../../types/evenement.types';
import {
  getProchainEvenements,
  joursRestants,
} from '../../services/evenement.service';
import {
  Calendar,
  ArrowRight,
  Users,
  UserCheck,
  CalendarClock,
  ShieldAlert,
  FileSignature,
  Scale,
  Gavel,
  Wrench,
  Banknote,
  Loader2,
} from 'lucide-react';

// ============================================
// MAP ICÔNES PAR TYPE
// ============================================

const ICONE_MAP: Record<TypeEvenement, React.ReactNode> = {
  ag: <Users className="w-4 h-4" />,
  reunion_cs: <UserCheck className="w-4 h-4" />,
  rdv: <CalendarClock className="w-4 h-4" />,
  echeance_garantie: <ShieldAlert className="w-4 h-4" />,
  echeance_contrat: <FileSignature className="w-4 h-4" />,
  echeance_juridique: <Scale className="w-4 h-4" />,
  audience: <Gavel className="w-4 h-4" />,
  travaux: <Wrench className="w-4 h-4" />,
  appel_fonds: <Banknote className="w-4 h-4" />,
  autre: <Calendar className="w-4 h-4" />,
};

// ============================================
// COULEUR ADAPTÉE AU CONTEXTE DASHBOARD
// Rouge = imminent (J-1/J-0), Orange = proche (J-7), Ambre = bientôt (J-14), Gris = serein
// ============================================

const getCouleurCompteARebours = (jours: number): string => {
  if (jours <= 1) return 'text-red-600 font-bold';
  if (jours <= 7) return 'text-orange-500 font-semibold';
  if (jours <= 14) return 'text-amber-500 font-medium';
  return 'text-gray-500 font-medium';
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const EcheancesWidget: React.FC = () => {
  const { userProfile } = useAuth();
  const coproprieteId = userProfile?.coproprieteIds?.[0];

  const [evenements, setEvenements] = useState<Evenement[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const charger = async () => {
      if (!coproprieteId) {
        setLoading(false);
        return;
      }
      try {
        const data = await getProchainEvenements(coproprieteId, 5);
        setEvenements(data);
      } catch (error) {
        console.warn('Erreur chargement prochaines échéances:', error);
      } finally {
        setLoading(false);
      }
    };
    charger();
  }, [coproprieteId]);

  // Ne rien afficher si pas de copropriété ou pas d'événements
  if (!coproprieteId) return null;
  if (!loading && evenements.length === 0) return null;

  // Formater la date courte
  const formatDateCourte = (evt: Evenement): string => {
    const d = evt.dateDebut.toDate();
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    };
    let result = d.toLocaleDateString('fr-FR', options);
    const h = d.getHours();
    const m = d.getMinutes();
    if (h !== 0 || m !== 0) {
      result += ` ${h.toString().padStart(2, '0')}h${m.toString().padStart(2, '0')}`;
    }
    return result;
  };

  return (
    <div className="mb-6 bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Calendar className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-800">Prochaines échéances</h2>
            {loading ? (
              <p className="text-sm text-gray-400">Chargement...</p>
            ) : (
              <p className="text-sm text-gray-500">
                {evenements.length} événement{evenements.length > 1 ? 's' : ''} à venir
              </p>
            )}
          </div>
        </div>
        <Link
          to="/admin/calendrier"
          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          Calendrier
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Contenu */}
      {loading ? (
        <div className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {evenements.map(evt => {
            const jours = joursRestants(evt.dateDebut);
            const couleurJours = getCouleurCompteARebours(jours);
            const couleurType = TYPE_EVENEMENT_COULEURS[evt.type] || 'bg-gray-500';

            return (
              <Link
                key={evt.id}
                to="/admin/calendrier"
                className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
              >
                {/* Barre couleur type */}
                <div className={`w-1.5 h-12 rounded-full ${couleurType} flex-shrink-0`} />

                {/* Icône */}
                <div className="flex-shrink-0 text-gray-400">
                  {ICONE_MAP[evt.type]}
                </div>

                {/* Contenu */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-800 text-sm truncate">{evt.titre}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{TYPE_EVENEMENT_LABELS[evt.type]}</span>
                    <span>·</span>
                    <span>{formatDateCourte(evt)}</span>
                  </div>
                </div>

                {/* Compte à rebours */}
                <div className={`flex-shrink-0 text-sm ${couleurJours}`}>
                  {jours === 0 ? "Aujourd'hui" :
                   jours === 1 ? 'Demain' :
                   `J-${jours}`}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EcheancesWidget;
